import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart, PGLineChart } from '../../components/primitives/PGChart';
import { getBranch, peersOf } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const { FiUsers, FiUserX, FiBookOpen, FiClock, FiHeart } = FiIcons;

const SKILLS = [
  'OEM Glass Install',
  'Primer Cure Spec',
  'ADAS Calibration',
  'Polyurethane Handling',
  'Customer Handover',
  'Safety / PPE',
  'Insurer Portal',
  'Job Card Audit',
];

const TECHS = ['T. Mokoena', 'R. Davids', 'N. Khumalo', 'L. Botha', 'J. Khoza', 'S. Naidoo', 'M. van Wyk'];

const HR_ACTIONS = [
  { title: 'Complete Q2 performance reviews', owner: 'Branch Mgr', due: '5d', tone: 'warn' },
  { title: 'Return-to-work interview (J. Khoza)', owner: 'HR Officer', due: '1d', tone: 'alert' },
  { title: 'Renew first-aid certification for 2 techs', owner: 'Safety Rep', due: '12d', tone: 'warn' },
  { title: 'Onboard new apprentice (M. Dube)', owner: 'Branch Mgr', due: '3d', tone: 'ok' },
];

function generateSkillGrid(seed = 0) {
  const states = ['ok', 'warn', 'ok', 'ok', 'alert', 'ok', 'warn', 'ok'];
  return TECHS.map((t, i) =>
    SKILLS.map((_, j) => states[(i + j + seed) % states.length])
  );
}

function generateLeaveSeries(seed = 1) {
  const arr = [];
  let v = 8;
  for (let i = 0; i < 12; i++) {
    v = Math.max(2, Math.min(18, v + (Math.sin(i * 0.7 + seed) * 3)));
    arr.push(+v.toFixed(1));
  }
  return arr;
}

export default function HRBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);

  const peers = branch ? peersOf(branch) : [];
  const peerAvg = useMemo(() => {
    if (!branch || peers.length === 0) return branch?.hr ?? { engagementScore: 0, absenteeism: 0, trainingComplete: 0, overtimeHrs: 0 };
    return {
      engagementScore: +(peers.reduce((s, p) => s + p.hr.engagementScore, 0) / peers.length).toFixed(1),
      absenteeism: +(peers.reduce((s, p) => s + p.hr.absenteeism, 0) / peers.length).toFixed(1),
      trainingComplete: +(peers.reduce((s, p) => s + p.hr.trainingComplete, 0) / peers.length).toFixed(1),
      overtimeHrs: Math.round(peers.reduce((s, p) => s + p.hr.overtimeHrs, 0) / peers.length),
    };
  }, [peers, branch]);

  const skillGrid = useMemo(() => generateSkillGrid((branch?.id ?? '').length), [branch]);
  const leaveSeries = useMemo(() => generateLeaveSeries((branch?.id ?? '').length), [branch]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  const dotColor = (t) => (t === 'ok' ? PG.emerald : t === 'warn' ? PG.amber : PG.red);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      {/* Branch heading */}
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">HR / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.hr.disciplinaryOpen > 0 ? 'alert' : 'ok'}>
          Peer-group: {branch.peerGroup}
        </StatusPill>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="HEADCOUNT" value={branch.hr.headcount} unit="FTE" icon={FiUsers} accent={PG.white} />
        <KPICard title="ABSENTEEISM" value={branch.hr.absenteeism} unit="%" icon={FiUserX} accent={branch.hr.absenteeism > 5 ? PG.red : branch.hr.absenteeism >= 3 ? PG.amber : PG.emerald} target="<3%" />
        <KPICard title="TRAINING COMPLETE" value={branch.hr.trainingComplete} unit="%" icon={FiBookOpen} accent={PG.emerald} target=">90%" />
        <KPICard title="OVERTIME HRS" value={branch.hr.overtimeHrs} unit="hrs" icon={FiClock} accent={PG.amber} />
        <KPICard title="ENGAGEMENT" value={branch.hr.engagementScore} unit="/10" icon={FiHeart} accent={PG.violet} target=">8.0" />
      </div>

      {/* Main */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="PEER_COMPARISON" subtitle={`vs ${peers.length} peer branches`} className="col-span-5">
          <div className="h-full p-2">
            <PGBarChart
              categories={['Engagement', 'Absenteeism', 'Training', 'Overtime']}
              series={[
                { name: branch.name, data: [branch.hr.engagementScore, branch.hr.absenteeism, branch.hr.trainingComplete, branch.hr.overtimeHrs], itemStyle: { color: PG.red } },
                { name: 'Peer Avg', data: [peerAvg.engagementScore, peerAvg.absenteeism, peerAvg.trainingComplete, peerAvg.overtimeHrs], itemStyle: { color: PG.neutral500 } },
              ]}
            />
          </div>
        </Panel>

        <Panel title="TRAINING_MATRIX" subtitle="skill × technician" className="col-span-7">
          <div className="h-full overflow-auto custom-scrollbar p-3">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 pb-2">Technician</th>
                  {SKILLS.map((s) => (
                    <th key={s} className="px-1 pb-2 font-mono-tech text-[8px] uppercase tracking-wider text-neutral-500 text-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 70 }}>
                      {s}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TECHS.map((t, i) => (
                  <tr key={t} className="border-t border-white/5">
                    <td className="py-2 pr-3 text-xs text-white">{t}</td>
                    {skillGrid[i].map((status, j) => (
                      <td key={j} className="px-1 text-center">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: dotColor(status), boxShadow: `0 0 6px ${dotColor(status)}66` }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="OPEN_HR_ACTIONS" subtitle={`${HR_ACTIONS.length} tasks`} className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {HR_ACTIONS.map((a, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div>
                  <div className="text-white text-sm">{a.title}</div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">{a.owner}</div>
                </div>
                <StatusPill tone={a.tone}>Due {a.due}</StatusPill>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="LEAVE_PATTERN" subtitle="last 12 weeks — days taken" className="col-span-7">
          <div className="h-full p-2">
            <PGLineChart
              categories={Array.from({ length: 12 }, (_, i) => `W${i + 1}`)}
              series={[{ name: 'Leave days', data: leaveSeries, itemStyle: { color: PG.cyan }, lineStyle: { color: PG.cyan, width: 2 } }]}
            />
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
